import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { useContext } from 'react';
import { UserContext } from 'context/userContext';
import { MobileMenuItemProps } from 'components/mobile/MobileMenuItem/MobileMenuItem';
import { Layout } from 'components/Layout/Layout';
import { SportsBetting } from 'components/SportsBetting/SportsBetting';
import LeagueContext from 'context/leagueContext';
import { toggleAppFullscreen } from 'utils/toggleAppFullscreen';
import { usePlayerLayout } from 'lib/playerLayout';
import { ModernHome } from 'components/mobile/ModernHome/ModernHome';
import { ClassicHome } from 'components/mobile/ClassicHome/ClassicHome';

// The sections the classic home shows as animated cards; the rest are tools.
const HOME_CARD_KEYS = [
  'sports-betting',
  'live-betting',
  'highlights',
  'live-casino',
  'casino-games',
];

export const Home = () => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedUser, logout } = useContext(UserContext);
  const layout = usePlayerLayout();

  const mobilemenuItems: MobileMenuItemProps[] = [
    {
      iconKey: 'sports-betting',
      label: t('sports_betting_label'),
      destinationUrl: '/sports-betting',
    },
    {
      iconKey: 'live-betting',
      label: t('live_betting_label'),
      destinationUrl: '/live-betting',
    },
    {
      iconKey: 'highlights',
      label: t('highlights_label'),
      destinationUrl: '/sports-betting/highlights',
    },
    {
      iconKey: 'live-casino',
      label: t('live_casino_label'),
      destinationUrl: '/live-casino',
    },
    {
      iconKey: 'casino-games',
      label: t('casino_games_label'),
      destinationUrl: '/casino-games',
    },
    {
      iconKey: 'results',
      label: t('results_label'),
      destinationUrl: '/results',
    },
    {
      iconKey: 'search',
      label: t('search_label'),
      destinationUrl: '/search',
    },
    {
      iconKey: 'contact',
      label: t('contact_label'),
      destinationUrl: '/contact',
    },
    {
      iconKey: 'language',
      label: t('language_label'),
      destinationUrl: '/language',
    },
    {
      iconKey: 'info',
      label: t('other_label'),
      destinationUrl: '/info',
    },
    {
      iconKey: 'fullscreen',
      label: t('app_fullscreen_label'),
      onClickAction: toggleAppFullscreen,
    },
    {
      iconKey: 'login',
      label: t('login_label'),
      destinationUrl: '/login',
    },
  ];

  const logoutItem: MobileMenuItemProps = {
    iconKey: 'logout',
    label: t('logout_label'),
    onClickAction: async () => {
      await logout();
    },
  };

  const themeItem: MobileMenuItemProps = {
    iconKey: 'theme',
    label: t('theme_settings_label'),
    destinationUrl: '/theme-settings',
  };

  const depositItem: MobileMenuItemProps = {
    iconKey: 'deposit',
    label: t('deposit_label'),
    onClickAction: () => navigate('/deposit'),
  };

  const addLoggedUserIcons = () => {
    return [
      ...mobilemenuItems.slice(0, 5),
      depositItem,
      ...mobilemenuItems.slice(5, mobilemenuItems.length - 1),
      themeItem,
      logoutItem,
    ];
  };

  return (
    <>
      {!isMobile && (
        <Layout whiteBg>
          {layout === 'modern' ? (
            <ModernHome desktop />
          ) : (
            <LeagueContext>
              <SportsBetting />
            </LeagueContext>
          )}
        </Layout>
      )}
      {isMobile && (
        <MobileLayout hideBack>
          {layout === 'modern' ? (
            <ModernHome />
          ) : (
            <ClassicHome
              tools={(!loggedUser
                ? mobilemenuItems
                : addLoggedUserIcons()
              ).filter((x) => !HOME_CARD_KEYS.includes(x.iconKey))}
            />
          )}
        </MobileLayout>
      )}
    </>
  );
};
